import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  FileText, Download, RefreshCw, Search, Save, Star, Trash2, ChevronLeft, ChevronRight, Filter,
  Clock, ListChecks, BarChart3, TableIcon, X, CheckCircle2, XCircle, Loader2, FileBarChart, Inbox,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid,
} from 'recharts';
import { chartColor, tooltipContentStyle, tooltipCursor, axisProps, gridProps } from '../lib/chartTheme';
import { EmptyState, CardSkeleton } from './common/States';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from './ui/table';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { reportEngineAPI } from '../services/api';
import { confirmToast } from '../lib/confirmToast';

const titleize = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const PAGE_SIZE = 25;

// A single metadata-driven filter control.
function FilterControl({ filter, value, onChange, dynamicOptions }) {
  if (filter.type === 'date_range') {
    return (
      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">{filter.label}</Label>
        <div className="flex items-center gap-1">
          <Input
            type="date"
            className="h-9"
            value={value?.date_from || ''}
            onChange={(e) => onChange({ ...value, date_from: e.target.value })}
          />
          <span className="text-muted-foreground">–</span>
          <Input
            type="date"
            className="h-9"
            value={value?.date_to || ''}
            onChange={(e) => onChange({ ...value, date_to: e.target.value })}
          />
        </div>
      </div>
    );
  }

  if (filter.type === 'select' || filter.type === 'multiselect') {
    const options = filter.options && filter.options.length
      ? filter.options
      : (dynamicOptions || []);
    return (
      <div className="flex flex-col gap-1 min-w-[160px]">
        <Label className="text-xs text-muted-foreground">{filter.label}</Label>
        <Select
          value={value || '__all__'}
          onValueChange={(v) => onChange(v === '__all__' ? '' : v)}
        >
          <SelectTrigger className="h-9"><SelectValue placeholder={`All ${filter.label}`} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All</SelectItem>
            {options.map((o) => (
              <SelectItem key={String(o.value)} value={String(o.value)}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  // text / number
  return (
    <div className="flex flex-col gap-1 min-w-[180px]">
      <Label className="text-xs text-muted-foreground">{filter.label}</Label>
      <Input
        className="h-9"
        type={filter.type === 'number' ? 'number' : 'text'}
        placeholder={filter.placeholder || filter.label}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

const formatCell = (value, type) => {
  if (value === null || value === undefined || value === '') return '—';
  if (type === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

export default function ReportEngine() {
  const [catalog, setCatalog] = useState({ categories: [], reports: [] });
  const [activeReportId, setActiveReportId] = useState(null);
  const [meta, setMeta] = useState(null);
  const [filterValues, setFilterValues] = useState({});
  const [dynamicOptions, setDynamicOptions] = useState({}); // { source: [options] }
  const [result, setResult] = useState(null);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [loading, setLoading] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [savedViews, setSavedViews] = useState([]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [exportFormat, setExportFormat] = useState('csv');
  const [viewMode, setViewMode] = useState('grid'); // grid | chart
  const [jobsOpen, setJobsOpen] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    name: '', frequency: 'monthly', hour: 6, day_of_week: 0, day_of_month: 1,
    export_format: 'excel', delivery: ['notification', 'download'],
  });

  const isGrouped = useMemo(
    () => (meta?.columns || []).length === 2 && (meta?.columns || [])[0]?.key === 'group',
    [meta]
  );

  // Load catalog once.
  useEffect(() => {
    (async () => {
      setCatalogLoading(true);
      try {
        const { data } = await reportEngineAPI.catalog();
        setCatalog(data);
        if (data.reports?.length) setActiveReportId(data.reports[0].id);
      } catch (e) {
        toast.error('Failed to load report catalog');
      } finally {
        setCatalogLoading(false);
      }
    })();
  }, []);

  // Build query params from current filter state.
  const buildParams = useCallback((extra = {}) => {
    const params = {};
    Object.entries(filterValues).forEach(([key, val]) => {
      if (val === '' || val === null || val === undefined) return;
      if (typeof val === 'object') {
        // date_range → date_from / date_to
        if (val.date_from) params.date_from = val.date_from;
        if (val.date_to) params.date_to = val.date_to;
      } else {
        params[key] = val;
      }
    });
    return { ...params, ...extra };
  }, [filterValues]);

  // Load metadata + saved views when the active report changes.
  useEffect(() => {
    if (!activeReportId) return;
    (async () => {
      setLoading(true);
      setResult(null);
      setFilterValues({});
      setDynamicOptions({});
      setPage(1);
      setSort(null);
      try {
        const { data } = await reportEngineAPI.metadata(activeReportId);
        setMeta(data);
        setSort(data.default_sort || null);
        setSortDir(data.default_sort_dir || 'asc');
        // Preload dynamic filter options.
        const sources = (data.filters || [])
          .map((f) => f.options_source)
          .filter(Boolean);
        const uniqueSources = [...new Set(sources)];
        const optionResults = await Promise.all(
          uniqueSources.map((s) =>
            reportEngineAPI.options(activeReportId, s).then((r) => [s, r.data.options]).catch(() => [s, []])
          )
        );
        setDynamicOptions(Object.fromEntries(optionResults));
        reportEngineAPI.listSavedViews(activeReportId)
          .then((r) => setSavedViews(r.data.data || []))
          .catch(() => setSavedViews([]));
      } catch (e) {
        toast.error('Failed to load report metadata');
        setMeta(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [activeReportId]);

  // Run the report (server-side pagination + sort).
  const runReport = useCallback(async (opts = {}) => {
    if (!activeReportId) return;
    const nextPage = opts.page ?? page;
    const nextSort = opts.sort ?? sort;
    const nextDir = opts.sortDir ?? sortDir;
    setLoading(true);
    try {
      const params = buildParams({
        page: nextPage,
        page_size: PAGE_SIZE,
        ...(nextSort ? { sort: nextSort, sort_dir: nextDir } : {}),
      });
      const { data } = await reportEngineAPI.run(activeReportId, params);
      setResult(data);
      setPage(data.page);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to run report');
    } finally {
      setLoading(false);
    }
  }, [activeReportId, page, sort, sortDir, buildParams]);

  // Auto-run when metadata loads.
  useEffect(() => {
    if (meta && activeReportId === meta.id) runReport({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta]);

  const handleSort = (colKey) => {
    const nextDir = sort === colKey && sortDir === 'asc' ? 'desc' : 'asc';
    setSort(colKey);
    setSortDir(nextDir);
    runReport({ sort: colKey, sortDir: nextDir, page: 1 });
  };

  const EXT = { csv: 'csv', excel: 'xlsx', pdf: 'pdf' };

  const handleExport = async () => {
    try {
      const params = buildParams(sort ? { sort, sort_dir: sortDir } : {});
      const res = await reportEngineAPI.export(activeReportId, params, exportFormat);
      const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${activeReportId}.${EXT[exportFormat] || 'csv'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      if (res.headers?.['x-report-truncated'] === '1') {
        toast.warning('Export truncated at 50,000 rows. Queue a background job or narrow filters.');
      } else {
        toast.success(`Exported ${exportFormat.toUpperCase()}`);
      }
    } catch (e) {
      toast.error('Export failed');
    }
  };

  const loadJobs = useCallback(async () => {
    try {
      const { data } = await reportEngineAPI.listJobs({ report_id: activeReportId, limit: 25 });
      setJobs(data.data || []);
    } catch (e) { /* silent */ }
  }, [activeReportId]);

  const queueJob = async () => {
    try {
      await reportEngineAPI.createJob({
        report_id: activeReportId,
        export_format: exportFormat === 'csv' ? 'excel' : exportFormat,
        params: buildParams(),
      });
      toast.success('Background export queued');
      setJobsOpen(true);
      loadJobs();
    } catch (e) {
      toast.error('Failed to queue export');
    }
  };

  const downloadJobArtifact = async (job) => {
    try {
      const res = await reportEngineAPI.downloadJob(job.id);
      const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = job.file_name || `report_${job.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const cancelJob = (job) => {
    confirmToast({
      message: 'Cancel this export job?',
      confirmLabel: 'Cancel Job',
      destructive: true,
      onConfirm: async () => {
        try { await reportEngineAPI.cancelJob(job.id); loadJobs(); toast.success('Job cancelled'); }
        catch (e) { toast.error('Could not cancel'); }
      },
    });
  };

  const retryJob = async (job) => {
    try { await reportEngineAPI.retryJob(job.id); loadJobs(); toast.success('Retrying export'); }
    catch (e) { toast.error('Retry failed'); }
  };

  // Poll jobs while the panel is open and any are in-flight.
  useEffect(() => {
    if (!jobsOpen) return undefined;
    const anyRunning = jobs.some((j) => ['queued', 'running'].includes(j.status));
    if (!anyRunning) return undefined;
    const t = setInterval(loadJobs, 1500);
    return () => clearInterval(t);
  }, [jobsOpen, jobs, loadJobs]);

  const createSchedule = async () => {
    if (!scheduleForm.name.trim()) { toast.error('Enter a schedule name'); return; }
    try {
      await reportEngineAPI.createSchedule({
        report_id: activeReportId,
        name: scheduleForm.name.trim(),
        frequency: scheduleForm.frequency,
        hour: Number(scheduleForm.hour),
        day_of_week: scheduleForm.frequency === 'weekly' ? Number(scheduleForm.day_of_week) : null,
        day_of_month: ['monthly', 'quarterly'].includes(scheduleForm.frequency) ? Number(scheduleForm.day_of_month) : null,
        export_format: scheduleForm.export_format,
        params: buildParams(),
        delivery: scheduleForm.delivery,
      });
      toast.success('Report scheduled');
      setScheduleOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to schedule');
    }
  };

  const handleSaveView = async () => {
    if (!saveName.trim()) { toast.error('Enter a name'); return; }
    try {
      await reportEngineAPI.createSavedView({
        report_id: activeReportId,
        name: saveName.trim(),
        config: { params: buildParams(), sort, sort_dir: sortDir },
      });
      toast.success('Report saved');
      setSaveDialogOpen(false);
      setSaveName('');
      const r = await reportEngineAPI.listSavedViews(activeReportId);
      setSavedViews(r.data.data || []);
    } catch (e) {
      toast.error('Failed to save report');
    }
  };

  const applySavedView = (view) => {
    const cfg = view.config || {};
    const restored = {};
    Object.entries(cfg.params || {}).forEach(([k, v]) => {
      if (k === 'date_from' || k === 'date_to') {
        restored.date_range = { ...(restored.date_range || {}), [k]: v };
      } else {
        restored[k] = v;
      }
    });
    setFilterValues(restored);
    if (cfg.sort) { setSort(cfg.sort); setSortDir(cfg.sort_dir || 'asc'); }
    setTimeout(() => runReport({ page: 1, sort: cfg.sort, sortDir: cfg.sort_dir }), 0);
    toast.success(`Loaded "${view.name}"`);
  };

  const deleteSavedView = (view) => {
    confirmToast({
      message: `Delete saved report "${view.name}"?`,
      description: 'This cannot be undone.',
      confirmLabel: 'Delete',
      destructive: true,
      onConfirm: async () => {
        try {
          await reportEngineAPI.deleteSavedView(view.id);
          setSavedViews((prev) => prev.filter((v) => v.id !== view.id));
          toast.success('Saved report deleted');
        } catch (e) {
          toast.error('Failed to delete');
        }
      },
    });
  };

  const reportsByCategory = useMemo(() => {
    const groups = {};
    (catalog.reports || []).forEach((r) => {
      if (categoryFilter !== 'all' && r.category !== categoryFilter) return;
      (groups[r.category] = groups[r.category] || []).push(r);
    });
    return groups;
  }, [catalog.reports, categoryFilter]);

  const activeReport = (catalog.reports || []).find((r) => r.id === activeReportId);
  const totalPages = result?.total_pages || 1;

  return (
    <div className="flex gap-4 h-full">
      {/* Catalog sidebar */}
      <div className="w-72 shrink-0">
        <Card className="h-full">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-4 w-4" /> Report Catalog
            </CardTitle>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="h-8 mt-2"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {(catalog.categories || []).map((c) => (
                  <SelectItem key={c.category} value={c.category}>
                    {c.category} ({c.report_count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent className="space-y-5 overflow-y-auto max-h-[calc(100vh-220px)]">
            {catalogLoading && <CardSkeleton rows={6} className="border-0 bg-transparent p-0" />}
            {Object.entries(reportsByCategory).map(([cat, reports]) => (
              <div key={cat}>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">{cat}</p>
                <div className="space-y-1">
                  {reports.map((r) => {
                    const active = activeReportId === r.id;
                    return (
                      <button
                        key={r.id}
                        onClick={() => setActiveReportId(r.id)}
                        className={`card-interactive w-full text-left flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-colors ${
                          active
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : 'text-foreground hover:bg-muted'
                        }`}
                      >
                        <span
                          className={`grid place-items-center h-7 w-7 shrink-0 rounded-lg ${
                            active ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-primary/10 text-primary'
                          }`}
                        >
                          <FileText className="h-3.5 w-3.5" />
                        </span>
                        <span className="truncate">{r.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Report runner */}
      <div className="flex-1 min-w-0">
        <Card className="h-full">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle className="text-lg">{activeReport?.name || 'Select a report'}</CardTitle>
                {activeReport?.description && (
                  <p className="text-sm text-muted-foreground mt-1">{activeReport.description}</p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                {isGrouped && (
                  <div className="flex rounded-md border overflow-hidden">
                    <button
                      className={`px-2 py-1.5 ${viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'bg-background'}`}
                      onClick={() => setViewMode('grid')} title="Grid">
                      <TableIcon className="h-4 w-4" />
                    </button>
                    <button
                      className={`px-2 py-1.5 ${viewMode === 'chart' ? 'bg-primary text-primary-foreground' : 'bg-background'}`}
                      onClick={() => setViewMode('chart')} title="Chart">
                      <BarChart3 className="h-4 w-4" />
                    </button>
                  </div>
                )}
                <Button variant="outline" size="sm" onClick={() => runReport({ page: 1 })} disabled={loading}>
                  <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Run
                </Button>
                <Button variant="outline" size="sm" onClick={() => setSaveDialogOpen(true)} disabled={!meta}>
                  <Save className="h-4 w-4 mr-1" /> Save
                </Button>
                <Button variant="outline" size="sm" onClick={() => { setScheduleForm((f) => ({ ...f, name: `${activeReport?.name} — schedule` })); setScheduleOpen(true); }} disabled={!meta}>
                  <Clock className="h-4 w-4 mr-1" /> Schedule
                </Button>
                <Button variant="outline" size="sm" onClick={() => { setJobsOpen(true); loadJobs(); }} disabled={!meta}>
                  <ListChecks className="h-4 w-4 mr-1" /> Jobs
                </Button>
                <Select value={exportFormat} onValueChange={setExportFormat}>
                  <SelectTrigger className="h-9 w-[92px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="csv">CSV</SelectItem>
                    <SelectItem value="excel">Excel</SelectItem>
                    <SelectItem value="pdf">PDF</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={handleExport} disabled={!result}>
                  <Download className="h-4 w-4 mr-1" /> Export
                </Button>
                <Button variant="outline" size="sm" onClick={queueJob} disabled={!meta} title="Run as a background job">
                  Queue
                </Button>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* Filters */}
            {meta?.filters?.length > 0 && (
              <div className="flex flex-wrap items-end gap-3 p-3 bg-muted/40 border border-border rounded-xl">
                <Filter className="h-4 w-4 text-muted-foreground mb-2.5" />
                {meta.filters.map((f) => (
                  <FilterControl
                    key={f.key}
                    filter={f}
                    value={filterValues[f.key]}
                    dynamicOptions={dynamicOptions[f.options_source]}
                    onChange={(v) => setFilterValues((prev) => ({ ...prev, [f.key]: v }))}
                  />
                ))}
                <Button size="sm" className="h-9" onClick={() => runReport({ page: 1 })}>
                  <Search className="h-4 w-4 mr-1" /> Apply
                </Button>
                {Object.keys(filterValues).length > 0 && (
                  <Button variant="ghost" size="sm" className="h-9"
                    onClick={() => { setFilterValues({}); setTimeout(() => runReport({ page: 1 }), 0); }}>
                    Clear
                  </Button>
                )}
              </div>
            )}

            {/* Saved views */}
            {savedViews.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground">Saved:</span>
                {savedViews.map((v) => (
                  <Badge key={v.id} variant="secondary" className="cursor-pointer gap-1 group">
                    <span onClick={() => applySavedView(v)} className="flex items-center gap-1">
                      {v.is_favorite && <Star className="h-3 w-3 fill-current" />}
                      {v.name}
                    </span>
                    {v.is_owner && (
                      <Trash2
                        className="h-3 w-3 opacity-50 hover:opacity-100"
                        onClick={(e) => { e.stopPropagation(); deleteSavedView(v); }}
                      />
                    )}
                  </Badge>
                ))}
              </div>
            )}

            {/* Chart view (grouped reports) */}
            {isGrouped && viewMode === 'chart' && (
              <div className="border border-border rounded-xl bg-card p-4" style={{ height: 360 }}>
                {(result?.data || []).length === 0 ? (
                  <EmptyState
                    icon={FileBarChart}
                    title="No data to chart"
                    description="Adjust your filters or run the report to visualize results."
                  />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={result.data} margin={{ top: 8, right: 16, bottom: 40, left: 8 }}>
                      <CartesianGrid {...gridProps} />
                      <XAxis dataKey="group" angle={-30} textAnchor="end" interval={0} height={60} {...axisProps} />
                      <YAxis allowDecimals={false} {...axisProps} />
                      <Tooltip contentStyle={tooltipContentStyle} cursor={tooltipCursor} />
                      <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                        {result.data.map((_, i) => (
                          <Cell key={i} fill={chartColor(i)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            )}

            {/* Result grid */}
            {(!isGrouped || viewMode === 'grid') && (
            <div className="border border-border rounded-xl overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {(result?.columns || meta?.columns || []).map((c) => (
                      <TableHead
                        key={c.key}
                        className={c.sortable ? 'cursor-pointer select-none whitespace-nowrap' : 'whitespace-nowrap'}
                        onClick={() => c.sortable && handleSort(c.key)}
                      >
                        {c.label}
                        {sort === c.key && (sortDir === 'asc' ? ' ▲' : ' ▼')}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={(meta?.columns || []).length || 1} className="p-4">
                        <CardSkeleton rows={6} className="border-0 bg-transparent p-0" />
                      </TableCell>
                    </TableRow>
                  ) : (result?.data || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={(meta?.columns || []).length || 1} className="p-0">
                        <EmptyState
                          icon={Inbox}
                          title="No records found"
                          description="No records match the current filters. Try adjusting or clearing them."
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    result.data.map((row, i) => (
                      <TableRow key={i}>
                        {result.columns.map((c) => (
                          <TableCell key={c.key} className="whitespace-nowrap">
                            {formatCell(row[c.key], c.type)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            )}

            {/* Pagination */}
            {result && result.total > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {result.total} record{result.total === 1 ? '' : 's'} · Page {result.page} of {totalPages}
                </span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled={page <= 1 || loading}
                    onClick={() => runReport({ page: page - 1 })}>
                    <ChevronLeft className="h-4 w-4" /> Prev
                  </Button>
                  <Button variant="outline" size="sm" disabled={page >= totalPages || loading}
                    onClick={() => runReport({ page: page + 1 })}>
                    Next <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Save dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Save Report</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Name</Label>
            <Input value={saveName} onChange={(e) => setSaveName(e.target.value)}
              placeholder={`${activeReport?.name} — my view`} />
            <p className="text-xs text-muted-foreground">
              Saves the current filters and sorting for {activeReport?.name}.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveView}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Jobs dialog */}
      <Dialog open={jobsOpen} onOpenChange={setJobsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Background Export Jobs</DialogTitle></DialogHeader>
          <div className="max-h-[420px] overflow-y-auto">
            {jobs.length === 0 ? (
              <EmptyState
                icon={ListChecks}
                title="No jobs yet"
                description="Use “Queue” to run an export in the background."
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Report</TableHead>
                    <TableHead>Format</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Rows</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {jobs.map((j) => (
                    <TableRow key={j.id}>
                      <TableCell className="text-xs">{j.name || j.report_id}</TableCell>
                      <TableCell className="uppercase text-xs">{j.export_format}</TableCell>
                      <TableCell>
                        <span className="flex items-center gap-1 text-xs">
                          {j.status === 'success' && <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />}
                          {j.status === 'failed' && <XCircle className="h-3.5 w-3.5 text-red-600" />}
                          {['queued', 'running'].includes(j.status) && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                          {j.status}{j.status === 'running' ? ` ${j.progress}%` : ''}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs">{j.row_count ?? '—'}</TableCell>
                      <TableCell className="text-right space-x-1">
                        {j.downloadable && (
                          <Button size="sm" variant="outline" onClick={() => downloadJobArtifact(j)}>
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {['queued', 'running'].includes(j.status) && (
                          <Button size="sm" variant="outline" onClick={() => cancelJob(j)}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {['failed', 'cancelled'].includes(j.status) && (
                          <Button size="sm" variant="outline" onClick={() => retryJob(j)}>
                            <RefreshCw className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={loadJobs}><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
            <Button onClick={() => setJobsOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule dialog */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Schedule Report</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={scheduleForm.name} onChange={(e) => setScheduleForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Frequency</Label>
                <Select value={scheduleForm.frequency} onValueChange={(v) => setScheduleForm((f) => ({ ...f, frequency: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Hour (UTC)</Label>
                <Input type="number" min={0} max={23} value={scheduleForm.hour}
                  onChange={(e) => setScheduleForm((f) => ({ ...f, hour: e.target.value }))} />
              </div>
              {scheduleForm.frequency === 'weekly' && (
                <div>
                  <Label>Day of Week</Label>
                  <Select value={String(scheduleForm.day_of_week)} onValueChange={(v) => setScheduleForm((f) => ({ ...f, day_of_week: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((d, i) => (
                        <SelectItem key={d} value={String(i)}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {['monthly', 'quarterly'].includes(scheduleForm.frequency) && (
                <div>
                  <Label>Day of Month</Label>
                  <Input type="number" min={1} max={28} value={scheduleForm.day_of_month}
                    onChange={(e) => setScheduleForm((f) => ({ ...f, day_of_month: e.target.value }))} />
                </div>
              )}
              <div>
                <Label>Format</Label>
                <Select value={scheduleForm.export_format} onValueChange={(v) => setScheduleForm((f) => ({ ...f, export_format: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="excel">Excel</SelectItem>
                    <SelectItem value="csv">CSV</SelectItem>
                    <SelectItem value="pdf">PDF</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Delivery</Label>
              <div className="flex gap-2 mt-1">
                {['notification', 'download', 'email'].map((ch) => {
                  const active = scheduleForm.delivery.includes(ch);
                  return (
                    <Badge key={ch} variant={active ? 'default' : 'outline'} className="cursor-pointer capitalize"
                      onClick={() => setScheduleForm((f) => ({
                        ...f,
                        delivery: active ? f.delivery.filter((d) => d !== ch) : [...f.delivery, ch],
                      }))}>
                      {ch}
                    </Badge>
                  );
                })}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Uses the current filters. Runs are delivered to the Notification Center and the background job queue.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleOpen(false)}>Cancel</Button>
            <Button onClick={createSchedule}>Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
